import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { FilterRequest } from '../models/virtual-store/filter-request.model';
import { JsonResult } from '../core/json-result.model';
import { map, takeUntil } from 'rxjs/operators';
import automapper from 'automapper-ts';
import { FilterResponse } from '../models/virtual-store/filter-response.model';
import { Product } from '../models/virtual-store/product.model';
import { ProductCategory } from '../models/virtual-store/product-category.model';
import { PaginationResult } from '../core/models/pagination-result.model';
import { DetailedOrderItem } from '../models/virtual-store/detailed-order-item.model';
import { Subject, BehaviorSubject, ReplaySubject } from 'rxjs';
import { ReniecValidationRequest } from '../models/virtual-store/reniec-validation-request.model';
import { CreditLine } from '../models/credit-line.model';
import { OrderState } from '../models/virtual-store/order-state.model';
import { EnvironmentService } from '../core/services/environment.service';
import { CommonService } from './common.service';
import { OrderItemUpdateResponse } from '../models/virtual-store/order-item-update-response.model';
import { Service } from '../models/virtual-store/service.model';

@Injectable({
  providedIn: 'root'
})
export class VirtualStoreService {

  private orderUpdatedAnnouncedSource = new BehaviorSubject<DetailedOrderItem[]>([]);
  orderUpdatedAnnounced = this.orderUpdatedAnnouncedSource.asObservable();

  announceOrderUpdated(orderItemList: DetailedOrderItem[]) {
    this.orderUpdatedAnnouncedSource.next(orderItemList);
  }

  private dniValidatedAnnouncedSource = new Subject<[string, string]>();
  dniValidatedAnnounced = this.dniValidatedAnnouncedSource.asObservable();

  announceDniValidated(data: [string, string]) {
    this.dniValidatedAnnouncedSource.next(data);
  }

  private creditLineUpdatedAnnouncedSource = new BehaviorSubject<CreditLine>(new CreditLine());
  creditLineUpdatedAnnounced = this.creditLineUpdatedAnnouncedSource.asObservable();

  announceCreditLineUpdated(creditLine: CreditLine) {
    this.creditLineUpdatedAnnouncedSource.next(creditLine);
  }
  constructor(private httpClient: HttpClient,
    private environmentService: EnvironmentService,
    private commonService: CommonService) { }

  getProducts(filterRequest: FilterRequest) {
    let url = `${this.environmentService.virtualOfficeServiceUrl}Product/GetProducts`;
    return this.httpClient.post<JsonResult<PaginationResult<Product>>>(url, filterRequest);
  }
  getProduct(id: number) {
    let url = `${this.environmentService.virtualOfficeServiceUrl}Product/DetailedProduct/${id}`;
    return this.httpClient.get<JsonResult<Product>>(url);
  }
  isDniValid(clientCode: string, dni: string) {
    let url = `${this.environmentService.virtualOfficeServiceUrl}VirtualStore/ValidateDni`;
    let httpParams = new HttpParams();
    httpParams = httpParams.append("dni", dni);
    httpParams = httpParams.append("clientCode", clientCode);
    return this.httpClient.get<JsonResult<any>>(url, { params: httpParams });
  }
  isPersonalDataValid(reniecValidationRequest: ReniecValidationRequest) {
    let url = `${this.environmentService.virtualOfficeServiceUrl}VirtualStore/ValidatePersonalData`;
    return this.httpClient.post<JsonResult<any>>(url, reniecValidationRequest);
  }
  getFilters(categoryCode: number) {
    let url = `${this.environmentService.virtualOfficeServiceUrl}Product/GetFilters`;
    let httpParams = new HttpParams();
    httpParams = httpParams.append("categoryCode", categoryCode.toString());
    return this.httpClient.get<JsonResult<FilterResponse[]>>(url, { params: httpParams }).pipe(takeUntil(this.commonService.unauthorizedAnnounced));
  }
  listCategories() {
    let url = `${this.environmentService.virtualOfficeServiceUrl}ProductsCategory/Available`;
    return this.httpClient.get<JsonResult<ProductCategory[]>>(url).pipe(takeUntil(this.commonService.unauthorizedAnnounced));
  }

  listMostBoughtProducts() {
    let url = `${this.environmentService.virtualOfficeServiceUrl}Product/MostBought`;
    return this.httpClient.get<JsonResult<Product[]>>(url).pipe(takeUntil(this.commonService.unauthorizedAnnounced));
  }

  listMostSeenProducts(isFnb : boolean) {
    
    let url = `${this.environmentService.virtualOfficeServiceUrl}Product/MostSeen`;
    let httpParams = new HttpParams();
    httpParams = httpParams.append('isFnb', isFnb.toString());
    return this.httpClient.get<JsonResult<Product[]>>(url,{ params: httpParams }).pipe(takeUntil(this.commonService.unauthorizedAnnounced));
  }

  listRelatedProducts(subCategoryId: number , isFnb : boolean) {
    let url = `${this.environmentService.virtualOfficeServiceUrl}Product/Related`;
    let httpParams = new HttpParams();
    httpParams = httpParams.append('subCategoryId', subCategoryId.toString());
    httpParams = httpParams.append('isFnb', isFnb.toString());
    return this.httpClient.get<JsonResult<Product[]>>(url, { params: httpParams }).pipe(takeUntil(this.commonService.unauthorizedAnnounced));
  }

  listInterestingProducts(productId: number , isFnb :boolean) {
    let url = `${this.environmentService.virtualOfficeServiceUrl}Product/Interesting`;
    let httpParams = new HttpParams();
    httpParams = httpParams.append('productId', productId.toString());
    httpParams = httpParams.append('isFnb', isFnb.toString());
    return this.httpClient.get<JsonResult<Product[]>>(url, { params: httpParams }).pipe(takeUntil(this.commonService.unauthorizedAnnounced));
  }
  listOrderStates() {
    let url = `${this.environmentService.virtualOfficeServiceUrl}VirtualStore/ListOrderStates`;
    return this.httpClient.get<JsonResult<OrderState[]>>(url).pipe(takeUntil(this.commonService.unauthorizedAnnounced));
  }

  private productFromCatalogPacket: Product;
  sendProductFromCatalogPacket(packet: Product) {
    this.productFromCatalogPacket = packet;
  }
  receiveProductFromCatalogPacket() {
    let packet = this.productFromCatalogPacket;
    this.productFromCatalogPacket = null;
    return packet;
  }

  canBuy(clientCode: string) {
    let url = `${this.environmentService.virtualOfficeServiceUrl}VirtualStore/ValidateSAPBuyPermission`;
    let httpParams = new HttpParams();
    httpParams = httpParams.append("clientCode", clientCode);
    return this.httpClient.get<JsonResult<any>>(url, { params: httpParams });
  }

  listRelatedServices(productId: number) {
    let url = `${this.environmentService.virtualOfficeServiceUrl}Product/RelatedServices`;
    let httpParams = new HttpParams();
    httpParams = httpParams.append('productId', productId.toString());
    return this.httpClient.get<JsonResult<Service[]>>(url, { params: httpParams }).pipe(takeUntil(this.commonService.unauthorizedAnnounced));
  }

  listRelatedServicesCart(productId: number) {
    let url = `${this.environmentService.virtualOfficeServiceUrl}Product/RelatedServicesCart`;
    let httpParams = new HttpParams();
    httpParams = httpParams.append('productId', productId.toString());
    return this.httpClient.get<JsonResult<Service[]>>(url, { params: httpParams }).pipe(takeUntil(this.commonService.unauthorizedAnnounced));
  }
}
